// The prompt rules as pure functions, adapted from common-shell's
// controllers/Prompt/queue.ts (ADR 0010). Every rule is about not answering
// for the user: a resolution they never made is indistinguishable, at the
// call site, from one they did.
import type { ReactNode } from 'react'

export type PromptTone = 'neutral' | 'primary' | 'danger'

export interface PromptAction {
  id: string
  label: string
  /** What the action means. `danger` also makes the prompt an alertdialog. */
  tone?: PromptTone
  /** The answer Escape and the scrim give. At most one; none → not dismissable. */
  cancel?: boolean
  /** Leading icon for an action (never for the cancel action). */
  icon?: ReactNode
}

export interface PromptRequest {
  title: string
  message?: ReactNode
  actions: PromptAction[]
}

/** Throws when a request cannot be answered unambiguously. */
export function validate(request: PromptRequest): void {
  const problems: string[] = []
  if (request.actions.length === 0) problems.push('has no actions, so there is no way to answer it')
  const ids = request.actions.map((a) => a.id)
  if (new Set(ids).size !== ids.length)
    problems.push('has duplicate action ids, so a resolution is ambiguous')
  if (request.actions.filter((a) => a.cancel).length > 1) {
    problems.push('has more than one cancel action, so Escape has no single answer')
  }
  if (problems.length > 0) throw new Error(`Prompt "${request.title}" ${problems.join('; ')}.`)
}

/** The action Escape and the scrim resolve to, or null. */
export function cancelAction(request: PromptRequest): PromptAction | null {
  return request.actions.find((a) => a.cancel) ?? null
}

/** Whether Escape or the scrim can dismiss the prompt at all. */
export function isDismissable(request: PromptRequest): boolean {
  return cancelAction(request) !== null
}

/**
 * The action focused on open, or null to focus the dialog itself. Never a
 * danger action: a stray Return would confirm it.
 */
export function defaultFocus(request: PromptRequest): PromptAction | null {
  const safe = request.actions.filter((a) => a.tone !== 'danger')
  return safe.find((a) => a.tone === 'primary') ?? safe.find((a) => a.cancel) ?? null
}

/** `alertdialog` when any action is destructive, so a screen reader stops. */
export function promptRole(request: PromptRequest): 'dialog' | 'alertdialog' {
  return request.actions.some((a) => a.tone === 'danger') ? 'alertdialog' : 'dialog'
}

/** Render order: the cancel action first, the rest as given. */
export function orderedActions(request: PromptRequest): PromptAction[] {
  return [...request.actions.filter((a) => a.cancel), ...request.actions.filter((a) => !a.cancel)]
}
